import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Projects", path: "/projects" },
    { name: "Contact", path: "/contact" },
    { name: "Support IQ", path: "/supportiq" },
    { name: "Deliver IQ", path: "/deliveriq" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-800/50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="nav-logo"
            className="flex items-center"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/sgfn7pb6_NASSAU%20LOGO.fw.png"
              alt="Nassau Engineering Ltd"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className="text-sm uppercase tracking-widest transition-colors duration-200 text-slate-400 hover:text-blue-400"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`text-sm uppercase tracking-widest transition-colors duration-200 ${
                    isActive(link.path)
                      ? "text-blue-400"
                      : "text-slate-400 hover:text-blue-400"
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a 
              href="tel:+35316301410"
              data-testid="nav-phone"
              className="flex items-center space-x-2 text-slate-400 hover:text-blue-400 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">+353 1 630 1410</span>
            </a>
            <Link to="/contact">
              <Button 
                data-testid="nav-cta-button"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase text-xs tracking-wider btn-glow"
              >
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-slate-400 hover:text-white"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden py-4 border-t border-slate-800/50"
          >
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                    className="text-sm uppercase tracking-widest py-2 text-slate-400 hover:text-blue-400"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                    className={`text-sm uppercase tracking-widest py-2 ${
                      isActive(link.path)
                        ? "text-blue-400"
                        : "text-slate-400 hover:text-blue-400"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <a 
                href="tel:+35316301410"
                className="flex items-center space-x-2 text-slate-400 hover:text-blue-400 transition-colors py-2"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm">+353 1 630 1410</span>
              </a>
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <Button 
                  data-testid="mobile-cta-button"
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase text-xs tracking-wider"
                >
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
